/**
 * The contact profile — the screen a rep spends their day on.
 *
 * A header that stays put (photo, name, status, primary number, tags) over a
 * two-column body: what the contact *is* on the right, what has *happened* on
 * the left. Sections rather than tabs, because a back-office user comparing
 * "they wanted the voice bot" against "we called them in March" should not have
 * to remember which tab held which.
 *
 * Every panel re-reads the whole contact after it saves. One extra query buys
 * a guarantee that the screen shows what the server actually stored — and the
 * server is where the interesting rules live (one primary number, custom-field
 * types, the backward-status guard), so optimistic updates here would be
 * guessing at their outcome.
 */
import { useQuery } from '@apollo/client';
import { Button, EmptyState, Spinner, StatusBadge } from '@meshhub/design-tokens/ui';
import { ArrowRight, MessageSquarePlus, RotateCcw, ShieldOff, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChannelIcons } from '../components/ChannelIcons.jsx';
import { ContactPhotoUpload } from '../components/ContactPhoto.web.jsx';
import { ConfirmDialog, MutationError } from '../components/FormKit.web.jsx';
import { DeleteContactDialog } from '../components/contact/DeleteContactDialog.web.jsx';
import { EraseDialog } from '../components/contact/EraseDialog.web.jsx';
import { DetailsPanel } from '../components/contact/DetailsPanel.web.jsx';
import { InteractionComposer } from '../components/contact/InteractionComposer.web.jsx';
import {
  AffiliationsPanel,
  InterestsPanel,
} from '../components/contact/InterestsPanel.web.jsx';
import { FilesPanel } from '../components/contact/FilesPanel.web.jsx';
import { PhonesPanel } from '../components/contact/PhonesPanel.web.jsx';
import { StatusDialog } from '../components/contact/StatusDialog.web.jsx';
import { TagPicker } from '../components/contact/TagPicker.web.jsx';
import { Timeline } from '../components/contact/Timeline.web.jsx';
import { ActivitiesPanel } from '../components/contact/ActivitiesPanel.web.jsx';
import { IssuesPanel } from '../components/contact/IssuesPanel.web.jsx';
import { MessagesPanel } from '../components/contact/MessagesPanel.web.jsx';
import { FollowUpDialog } from '../components/contact/FollowUpDialog.web.jsx';
import { useMutation } from '@apollo/client';
import { COLLABORATION_STATUS_LABELS, formatPhone } from '../lib/format.js';
import {
  ADMIN_LOOKUPS_QUERY,
  CONTACT_QUERY,
  UNDELETE_CONTACT,
} from '../lib/queries.js';
import { useSession } from '../lib/session.jsx';
import { useShortcut } from '../lib/shortcuts.jsx';
import { S } from '../lib/strings.js';

export function ContactDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useSession();

  const [composerOpen, setComposerOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [undeleteOpen, setUndeleteOpen] = useState(false);
  const [eraseOpen, setEraseOpen] = useState(false);
  const [timelineToken, setTimelineToken] = useState(0);
  const [followUpFor, setFollowUpFor] = useState(null);
  const [actionError, setActionError] = useState(null);

  useShortcut('Escape', () => navigate('/'), {
    description: S.shortcuts.actions.backToList,
    group: S.shortcuts.groups.contacts,
  });
  useShortcut('i', () => setComposerOpen(true), {
    description: S.shortcuts.actions.recordInteraction,
    group: S.shortcuts.groups.contacts,
  });

  const { data, loading, error, refetch } = useQuery(CONTACT_QUERY, { variables: { id } });
  const { data: typeData } = useQuery(ADMIN_LOOKUPS_QUERY, {
    variables: { kind: 'INTERACTION_TYPE', includeInactive: true },
  });
  const [undeleteContact] = useMutation(UNDELETE_CONTACT);

  const contact = data?.contact;

  if (loading && !contact) {
    return (
      <div className="flex justify-center p-7">
        <Spinner />
      </div>
    );
  }

  if (error || !contact) {
    return (
      <EmptyState
        title={S.contacts.detail.notFound}
        hint={error?.message}
        className="text-danger"
      />
    );
  }

  const isDeleted = Boolean(contact.deletedAt);
  // A deleted contact is readable — history points at it — but not editable.
  const readOnly = isDeleted;

  const run = async (fn) => {
    setActionError(null);
    try {
      await fn();
      await refetch();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div className="flex h-screen flex-col">
      <header className="flex shrink-0 items-start gap-5 border-b border-border bg-surface px-6 py-4">
        <ContactPhotoUpload
          contactId={contact.id}
          name={contact.displayName}
          url={contact.photoUrl}
          gender={contact.gender}
          onUploaded={refetch}
          disabled={readOnly}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/"
              className="flex items-center gap-1 text-sm text-text-muted hover:text-text"
            >
              <ArrowRight size={15} aria-hidden="true" />
              {S.nav.contacts}
            </Link>
            <h1 className="text-lg font-bold">{contact.displayName}</h1>
            <button
              type="button"
              disabled={readOnly}
              onClick={() => setStatusOpen(true)}
              title={S.contacts.detail.changeStatus}
              className="disabled:cursor-default"
            >
              <StatusBadge
                status={contact.collaborationStatus}
                label={COLLABORATION_STATUS_LABELS[contact.collaborationStatus]}
              />
            </button>
            {contact.primaryPhone && (
              <span className="flex items-center gap-2">
                <span dir="ltr" className="tabular-nums text-base">
                  {formatPhone(contact.primaryPhone.number)}
                </span>
                <ChannelIcons channels={contact.primaryPhone.channels} />
              </span>
            )}
          </div>

          <TagPicker contact={contact} onSaved={refetch} readOnly={readOnly} />

          {isDeleted && (
            <p role="status" className="rounded border border-danger px-4 py-2 text-sm text-danger">
              {S.contacts.deletedNotice}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-3">
          {!isDeleted && (
            <>
              <Button variant="primary" onClick={() => setComposerOpen(true)}>
                <MessageSquarePlus size={14} aria-hidden="true" />
                {S.contacts.detail.recordInteraction}
              </Button>
              <Button onClick={() => setStatusOpen(true)}>
                {S.contacts.detail.changeStatus}
              </Button>
              {/* LABELLED, not a bare icon: an unlabelled ghost trash can in
                  the corner of the header was missed by the people looking
                  for it. */}
              <Button variant="ghost" onClick={() => setDeleteOpen(true)}>
                <Trash2 size={14} aria-hidden="true" />
                {S.contacts.detail.deleteContact}
              </Button>
            </>
          )}
          {isDeleted && isAdmin && (
            <Button variant="primary" onClick={() => setUndeleteOpen(true)}>
              <RotateCcw size={14} aria-hidden="true" />
              {S.contacts.detail.undeleteContact}
            </Button>
          )}
          {/* ERASURE IS OFFERED ONLY ON AN ALREADY-DELETED CONTACT, and that
              ordering is deliberate: soft delete is the reversible step
              somebody takes when a contact is finished with, and erasure is
              the irreversible one they take when the contact ASKS. Putting
              them side by side on a live contact would put an undoable and an
              undoable-looking button next to each other. */}
          {isDeleted && isAdmin && (
            <Button variant="ghost" onClick={() => setEraseOpen(true)}>
              <ShieldOff size={14} aria-hidden="true" />
              {S.erasure.action}
            </Button>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-auto p-6">
        <MutationError error={actionError} />

        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-6">
          <div className="flex flex-col gap-6">
            <DetailsPanel contact={contact} onSaved={refetch} readOnly={readOnly} />
            <PhonesPanel contact={contact} onSaved={refetch} readOnly={readOnly} />
            <InterestsPanel contact={contact} onSaved={refetch} readOnly={readOnly} />
            <AffiliationsPanel contact={contact} onSaved={refetch} readOnly={readOnly} />
            <FilesPanel contact={contact} onSaved={refetch} readOnly={readOnly} />
            {/* Step 5. Sections rather than tabs, like everything else on this
                screen: a back-office user comparing two facts should not have
                to remember which tab held which. */}
            {!readOnly && <MessagesPanel contact={contact} onChanged={refetch} />}
            {!readOnly && <IssuesPanel contact={contact} />}
          </div>

          <div className="flex flex-col gap-6">
            {/* Step 4: what is PLANNED, above what has HAPPENED. A rep opening
                a profile is usually asking "what do I owe this person", and the
                answer to that is the calendar, not the history. */}
            {!readOnly && <ActivitiesPanel contactId={contact.id} />}
            <Timeline
              contactId={contact.id}
              types={typeData?.lookups ?? []}
              reloadToken={timelineToken}
              onFollowUp={readOnly ? undefined : setFollowUpFor}
            />
          </div>
        </div>
      </div>

      <InteractionComposer
        open={composerOpen}
        onOpenChange={setComposerOpen}
        contactId={contact.id}
        onRecorded={async () => {
          setTimelineToken((token) => token + 1);
          await refetch();
        }}
      />

      {/* Closes Step 3's deferral: an interaction's follow-up is a real
          activity now, created and linked in one saga (CLAUDE.md §8b). */}
      <FollowUpDialog
        interaction={followUpFor}
        contactId={contact.id}
        onOpenChange={(open) => !open && setFollowUpFor(null)}
        onCreated={() => {
          setTimelineToken((token) => token + 1);
          setFollowUpFor(null);
        }}
      />

      <StatusDialog
        open={statusOpen}
        onOpenChange={setStatusOpen}
        contact={contact}
        onChanged={refetch}
      />

      <EraseDialog
        open={eraseOpen}
        onOpenChange={setEraseOpen}
        contact={contact}
        // Back to the list: the contact still exists, but there is nothing
        // left on this page to look at.
        onErased={() => navigate('/contacts')}
      />

      <DeleteContactDialog
        contact={contact}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={refetch}
        onError={setActionError}
      />

      <ConfirmDialog
        open={undeleteOpen}
        onOpenChange={setUndeleteOpen}
        title={S.contacts.detail.undeleteContact}
        message={S.contacts.detail.confirmUndelete(contact.displayName)}
        confirmLabel={S.app.restore}
        onConfirm={() => run(() => undeleteContact({ variables: { id: contact.id } }))}
      />
    </div>
  );
}
